import { useEffect } from 'react';
import Lenis from 'lenis';
import { isInteractionLocked, subscribeInteractionLock } from '../utils/interactionLock';
import { GUIDED_SCROLL_EVENT, SCRIPTED_SCROLL_EVENT, type ScriptedScroll } from '../utils/pixelActions';

/**
 * Inertia-based smooth scrolling for the whole page.
 * Scrolls the real window, so native scroll listeners (e.g. the drone) keep working.
 * Skipped for visitors who prefer reduced motion.
 */
export function useSmoothScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const lenis = new Lenis({
      // Light glide: short settle, gentle curve, slightly shorter travel per wheel tick
      duration: 0.35,
      easing: (t) => 1 - Math.pow(1 - t, 2),
      wheelMultiplier: 0.95,
      smoothWheel: true,
      // Touch keeps the platform's native momentum
      syncTouch: false,
    });

    // In-page anchor links glide too
    const onClick = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest('a[href^="#"]');
      if (!link) return;
      const hash = link.getAttribute('href') ?? '#';
      const target = hash === '#' ? 0 : document.querySelector(hash);
      if (target === null) return;
      e.preventDefault();
      lenis.scrollTo(target as HTMLElement | number, { offset: 0 });
    };
    document.addEventListener('click', onClick);

    // Pixel's guided tour: a slower, deliberate glide to the requested section
    const onGuided = (e: Event) => {
      const target = document.querySelector((e as CustomEvent<string>).detail);
      if (target) lenis.scrollTo(target as HTMLElement, { offset: 0, duration: 1.8, easing: (t) => 1 - Math.pow(1 - t, 3) });
    };
    window.addEventListener(GUIDED_SCROLL_EVENT, onGuided);

    // Scripted scrolls (Pixel's lift-off rushes the whole page back up to the top)
    const onScripted = (e: Event) => {
      const { y, duration, accelerate } = (e as CustomEvent<ScriptedScroll>).detail;
      lenis.scrollTo(y, {
        duration,
        force: true,
        easing: accelerate ? (t) => t * t * t * t : (t) => 1 - Math.pow(1 - t, 3),
      });
    };
    window.addEventListener(SCRIPTED_SCROLL_EVENT, onScripted);

    // Freeze the glide while the site is locked (Pixel's full-charge short)
    const syncLock = () => {
      if (isInteractionLocked()) lenis.stop();
      else lenis.start();
    };
    const unsubscribeLock = subscribeInteractionLock(syncLock);

    // Keep the scrollable length in sync as the page grows (fonts, images, the 3D model loading in).
    // A stale length stopped the scroll short of the page end in some browsers, hiding the footer's bottom.
    const ro = new ResizeObserver(() => lenis.resize());
    ro.observe(document.body);
    const onLoad = () => lenis.resize();
    window.addEventListener('load', onLoad);

    let frame = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      window.removeEventListener('load', onLoad);
      document.removeEventListener('click', onClick);
      window.removeEventListener(GUIDED_SCROLL_EVENT, onGuided);
      window.removeEventListener(SCRIPTED_SCROLL_EVENT, onScripted);
      unsubscribeLock();
      lenis.destroy();
    };
  }, []);
}
